import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@hooks/useApiMutation';
import { taskRequestApi } from '../services/taskRequestApi';
import type { TaskRequest, ChecklistStep } from '../types/taskRequest';

export interface TaskPayload {
  customerName?: string;
  locationName?: string;
  address?: {
    street?: string;
    suite?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  checklist?: ChecklistStep[];
}

interface UseTaskDataResult {
  task: TaskRequest | undefined;
  isLoading: boolean;
  error: Error | null;
  payload: TaskPayload | null;
  refreshTask: () => void;
  /** Change status to New or InProgress. */
  changeStatus: (newStatus: 'New' | 'InProgress') => Promise<void>;
  statusLoading: boolean;
}

/**
 * Encapsulates fetching a task request by ID, parsing the requestPayload,
 * and status change mutations.
 * Keeps TaskDetailPage focused on layout and dialog orchestration.
 */
export function useTaskData(taskId: number): UseTaskDataResult {
  const queryClient = useQueryClient();

  const { data: task, isLoading, error } = useQuery({
    queryKey: ['task-request', taskId],
    queryFn: () => taskRequestApi.getTaskRequest(taskId),
    enabled: !Number.isNaN(taskId) && taskId > 0,
  });

  const payload = useMemo<TaskPayload | null>(() => {
    if (!task?.requestPayload) return null;
    try {
      return JSON.parse(task.requestPayload) as TaskPayload;
    } catch {
      return null;
    }
  }, [task?.requestPayload]);

  const refreshTask = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['task-request', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-request-comments', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-requests'] });
    queryClient.invalidateQueries({ queryKey: ['task-request-counts'] });
  }, [queryClient, taskId]);

  const statusMutation = useApiMutation(
    useCallback(async (newStatus: 'New' | 'InProgress') => {
      await taskRequestApi.updateTaskRequestStatus(taskId, newStatus);
    }, [taskId]),
    { onSuccess: refreshTask, errorMessage: 'Failed to update status' },
  );

  const changeStatus = async (newStatus: 'New' | 'InProgress') => {
    if (statusMutation.loading) return;
    await statusMutation.mutate(newStatus);
  };

  return {
    task,
    isLoading,
    error: error as Error | null,
    payload,
    refreshTask,
    changeStatus,
    statusLoading: statusMutation.loading,
  };
}
